import { useEffect, useRef, useState } from 'react'
import { PageHeader, FormField, Alert, btnPrimary, input, colors } from '../../components/ui'
import { api, ApiError } from '../../services/api'

const LOG_LEVELS = ['trace', 'debug', 'info', 'warn', 'error', 'fatal'] as const
type LogLevel = (typeof LOG_LEVELS)[number]

interface LogRetentionSettings {
  readonly retentionDays: number
  readonly minLevel: LogLevel
}

export function LogRetentionPanel() {
  const [retentionDays, setRetentionDays] = useState<number>(30)
  const [minLevel, setMinLevel] = useState<LogLevel>('info')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const cancelledRef = useRef(false)

  useEffect(() => {
    cancelledRef.current = false
    api
      .get<LogRetentionSettings>('/admin/settings/log-retention')
      .then((res) => {
        if (cancelledRef.current) return
        if (res.data) {
          setRetentionDays(res.data.retentionDays)
          setMinLevel(res.data.minLevel as LogLevel)
        }
        setLoading(false)
      })
      .catch((err) => {
        if (cancelledRef.current) return
        setError(err instanceof ApiError ? err.message : 'Failed to load settings')
        setLoading(false)
      })
    return () => {
      cancelledRef.current = true
    }
  }, [])

  async function handleSave() {
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const res = await api.patch<LogRetentionSettings>('/admin/settings/log-retention', {
        retentionDays,
        minLevel,
      })
      if (res.data) {
        setRetentionDays(res.data.retentionDays)
        setMinLevel(res.data.minLevel as LogLevel)
      }
      setSuccess('Saved.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div style={{ color: colors.textMuted }}>Loading...</div>

  return (
    <div>
      <PageHeader title="Logging" />
      <p style={{ color: colors.textMuted, fontSize: '0.875rem', margin: '0 0 1.25rem' }}>
        Configure edge log retention and minimum log level. Retention is enforced daily at 02:00 via{' '}
        <code>journalctl --vacuum-time</code>. Min level takes effect on the next API restart.
      </p>
      {error && <Alert type="error" message={error} />}
      {success && <Alert type="success" message={success} />}
      <FormField name="retentionDays" label="Retention (days)">
        <input
          id="retentionDays"
          type="number"
          min={1}
          max={365}
          value={retentionDays}
          onChange={(e) => setRetentionDays(Number(e.target.value))}
          style={{ ...input, width: '8rem' }}
        />
      </FormField>
      <FormField name="minLevel" label="Minimum log level">
        <select
          id="minLevel"
          value={minLevel}
          onChange={(e) => setMinLevel(e.target.value as LogLevel)}
          style={{ ...input, width: '10rem' }}
        >
          {LOG_LEVELS.map((level) => (
            <option key={level} value={level}>
              {level}
            </option>
          ))}
        </select>
      </FormField>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
        <button type="button" onClick={handleSave} style={btnPrimary} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  )
}
